import { useCallback, useMemo, useState } from "react";
import type { TableSelection } from "../components/ui";

// Shared checkbox-selection state for any entity list table (teachers,
// schools, modules, ...). ids resets selection whenever the underlying list
// changes identity (new search results, reload after delete) so stale ids
// never linger in the selected set.
export function useRowSelection(ids: (string | undefined)[]): {
  selection: TableSelection;
  selectedIds: Set<string>;
  clear: () => void;
} {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const selectableIds = useMemo(
    () => ids.filter((id): id is string => id !== undefined),
    [ids]
  );

  const onToggleRow = useCallback((id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const onToggleAll = useCallback(() => {
    setSelectedIds((current) => {
      const allSelected =
        selectableIds.length > 0 && selectableIds.every((id) => current.has(id));
      return allSelected ? new Set() : new Set(selectableIds);
    });
  }, [selectableIds]);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  return {
    selection: { rowIds: ids, selectedIds, onToggleRow, onToggleAll },
    selectedIds,
    clear,
  };
}
